import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import SEO from '../components/SEO';

const AboutPage = () => {
    const { t } = useTranslation();

    const specializations = [
        { 
            title: t('about.s1_title'), 
            desc: t('about.s1_desc'),
            icon: (
                <svg className="w-5 h-5 text-terracotta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
            )
        },
        { 
            title: t('about.s2_title'), 
            desc: t('about.s2_desc'),
            icon: (
                <svg className="w-5 h-5 text-terracotta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
            )
        },
        { 
            title: t('about.s3_title'), 
            desc: t('about.s3_desc'),
            icon: (
                <svg className="w-5 h-5 text-terracotta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
            )
        },
        { 
            title: t('about.s4_title'), 
            desc: t('about.s4_desc'),
            icon: (
                <svg className="w-5 h-5 text-terracotta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
            )
        },
        { 
            title: t('about.s5_title'), 
            desc: t('about.s5_desc'),
            icon: (
                <svg className="w-5 h-5 text-terracotta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
            )
        },
        { 
            title: t('about.s6_title'), 
            desc: t('about.s6_desc'),
            icon: (
                <svg className="w-5 h-5 text-terracotta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
            )
        }
    ];

    return (
        <>
            <SEO 
                title="Despre Oana Tenea - Psihoterapeut Cognitiv-Comportamental București & Online"
                description="Oana Tenea, psihoterapeut acreditat de Colegiul Psihologilor din România, specialist Schema Therapy certificat ISST, cu formare în CBT, EMDR și EFT. Cabinet în București, Calea Dorobanți 116-122 și ședințe online."
                path="/despre"
            />
            
            <div className="bg-stone-50 dark:bg-stone-950 text-stone-800 dark:text-stone-200 py-16 sm:py-24 transition-colors duration-300">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
                    
                    {/* Header Banner */}
                    <div className="text-center max-w-3xl mx-auto mb-16 sm:mb-20">
                        <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-terracotta-50 dark:bg-stone-800 text-terracotta dark:text-terracotta-light text-xs font-semibold tracking-wider uppercase mb-4 border border-terracotta-200/60 dark:border-stone-700">
                            Cunoaștere & Filosofie Clinică
                        </span>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900 dark:text-stone-100 tracking-tight">
                            {t('about.title')}
                        </h1>
                        <div className="w-16 h-1 bg-terracotta mx-auto mt-6 rounded-full"></div>
                    </div>

                    {/* Main Content Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                        
                        {/* Left Column: Portrait & Quick Credentials */}
                        <div className="lg:col-span-5 space-y-8">
                            
                            {/* Portrait Frame */}
                            <div className="relative max-w-md mx-auto">
                                <div className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-tr from-terracotta/20 via-amber-200/20 to-sage/20 transform rotate-2 filter blur-md"></div>
                                
                                <div className="relative rounded-[2.5rem] overflow-hidden bg-stone-200 dark:bg-stone-800 shadow-elevated border-4 border-white dark:border-stone-800 aspect-[4/5]">
                                    <img 
                                        src="/3DX_2399web.jpg" 
                                        alt="Oana Tenea, psihoterapeut cognitiv-comportamental" 
                                        className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                                        onError={(e) => { e.target.onerror = null; e.target.src='/OanaTenea.png'; }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-stone-950/70 via-transparent to-transparent"></div>
                                    <div className="absolute bottom-6 left-6 right-6 text-white">
                                        <div className="font-display text-2xl font-bold">Oana Tenea</div>
                                        <div className="text-xs text-stone-200 font-medium">București, Calea Dorobanți & Online</div>
                                    </div>
                                </div>
                            </div>

                            {/* Official Qualifications Card */}
                            <div className="bg-white dark:bg-stone-900 rounded-3xl p-8 shadow-elevated border border-stone-200/80 dark:border-stone-800 transition-colors">
                                <h3 className="font-display text-xl font-bold text-stone-900 dark:text-stone-100 mb-6 flex items-center">
                                    <div className="w-8 h-8 rounded-lg bg-terracotta/10 text-terracotta flex items-center justify-center mr-3 flex-shrink-0">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    {t('about.qualifications')}
                                </h3>

                                <div className="space-y-4">
                                    <div className="flex items-start space-x-3.5 p-3 rounded-2xl bg-stone-50 dark:bg-stone-800/60 border border-stone-200/60 dark:border-stone-700">
                                        <div className="w-2.5 h-2.5 bg-terracotta rounded-full mt-2 flex-shrink-0"></div>
                                        <p className="text-stone-700 dark:text-stone-300 text-sm font-medium">{t('about.q1')}</p>
                                    </div>

                                    <div className="flex items-start space-x-3.5 p-3 rounded-2xl bg-stone-50 dark:bg-stone-800/60 border border-stone-200/60 dark:border-stone-700">
                                        <div className="w-2.5 h-2.5 bg-terracotta rounded-full mt-2 flex-shrink-0"></div>
                                        <p className="text-stone-700 dark:text-stone-300 text-sm font-medium">{t('about.q2')}</p>
                                    </div>

                                    <div className="flex items-start space-x-3.5 p-3 rounded-2xl bg-stone-50 dark:bg-stone-800/60 border border-stone-200/60 dark:border-stone-700">
                                        <div className="w-2.5 h-2.5 bg-terracotta rounded-full mt-2 flex-shrink-0"></div>
                                        <p className="text-stone-700 dark:text-stone-300 text-sm font-medium">{t('about.q3')}</p>
                                    </div>

                                    <div className="flex items-start space-x-3.5 p-3 rounded-2xl bg-stone-50 dark:bg-stone-800/60 border border-stone-200/60 dark:border-stone-700">
                                        <div className="w-2.5 h-2.5 bg-terracotta rounded-full mt-2 flex-shrink-0"></div>
                                        <p className="text-stone-700 dark:text-stone-300 text-sm font-medium">{t('about.q4')}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Quick Conversation CTA Card */}
                            <div className="bg-gradient-to-br from-terracotta-50 to-amber-50/50 dark:from-stone-900 dark:to-stone-800 rounded-3xl p-8 border border-terracotta-200/60 dark:border-stone-700 shadow-soft">
                                <h4 className="font-display text-xl font-bold text-stone-900 dark:text-stone-100 mb-3">
                                    {t('about.contact_title')}
                                </h4>
                                <p className="text-stone-600 dark:text-stone-400 text-sm mb-6 leading-relaxed">
                                    {t('about.contact_desc')}
                                </p>
                                <Link 
                                    to="/contact" 
                                    className="inline-flex items-center px-6 py-3 bg-terracotta hover:bg-terracotta-dark text-white text-sm font-semibold rounded-full shadow-warm hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5"
                                >
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                    </svg>
                                    {t('about.contact_btn')}
                                </Link>
                            </div>

                        </div>

                        {/* Right Column: Personal Story, Therapeutic Philosophy & Specializations */}
                        <div className="lg:col-span-7 space-y-10">
                            
                            {/* Personal Welcome Card */}
                            <div className="bg-white dark:bg-stone-900 rounded-3xl p-8 sm:p-10 shadow-elevated border border-stone-200/80 dark:border-stone-800 transition-colors">
                                <span className="inline-block text-xs font-semibold uppercase tracking-wider text-terracotta mb-2">
                                    Despre Mine
                                </span>
                                <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 dark:text-stone-100 mb-6">
                                    {t('about.welcome')}
                                </h2>
                                
                                <div className="space-y-6 text-stone-700 dark:text-stone-300 leading-relaxed text-base sm:text-lg">
                                    <div className="bg-terracotta-50/70 dark:bg-stone-800/80 rounded-2xl p-6 border-l-4 border-terracotta text-stone-800 dark:text-stone-200">
                                        <p className="font-medium text-base sm:text-lg">
                                            {t('about.story1')}
                                        </p>
                                    </div>
                                    
                                    <p>
                                        <Trans i18nKey="about.story2">
                                            Dar nu în ultimul rând, sunt <strong className="text-stone-900 dark:text-white font-semibold">soție, mamă și prietenă</strong>. Dorința de a relaționa cu o gamă largă de oameni, din diferite medii multiculturale, m-a îndreptat către alegerea acestei profesii. Curiozitatea, prin natura mea, mă sprijină ca un tovarăș în calea cunoașterii de sine și a celor din jurul meu.
                                        </Trans>
                                    </p>
                                    
                                    <p>
                                        {t('about.story3')}
                                    </p>
                                </div>
                            </div>

                            {/* Therapeutic Approach Card */}
                            <div className="bg-white dark:bg-stone-900 rounded-3xl p-8 sm:p-10 shadow-elevated border border-stone-200/80 dark:border-stone-800 transition-colors">
                                <h3 className="font-display text-2xl sm:text-3xl font-bold text-stone-900 dark:text-stone-100 mb-6 flex items-center">
                                    <div className="w-8 h-8 rounded-lg bg-terracotta/10 text-terracotta flex items-center justify-center mr-3 flex-shrink-0">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                        </svg>
                                    </div>
                                    {t('about.approach_title')}
                                </h3>
                                
                                <div className="space-y-6">
                                    <p className="text-stone-700 dark:text-stone-300 leading-relaxed text-base sm:text-lg">
                                        <Trans i18nKey="about.approach_desc1">
                                            Folosesc o <strong className="text-stone-900 dark:text-white font-semibold">abordare integrativă</strong>, cu o bază solidă în terapia cognitiv-comportamentală (CBT), completată de tehnici din terapia centrată pe compasiune și mindfulness. Aceasta înseamnă că vom lucra împreună, în mod colaborativ, pentru a identifica tiparele de gândire și comportament care îți provoacă suferință și le vom înlocui cu altele noi, mai sănătoase și mai adaptative.
                                        </Trans>
                                    </p>
                                    
                                    <div className="bg-stone-50 dark:bg-stone-800/70 rounded-2xl p-6 sm:p-8 border border-stone-200/80 dark:border-stone-700 relative">
                                        <div className="text-terracotta/20 font-display text-5xl absolute top-3 left-4 select-none leading-none">“</div>
                                        <p className="font-display italic text-lg sm:text-xl text-stone-800 dark:text-stone-200 leading-relaxed relative z-10 pl-6">
                                            <Trans i18nKey="about.approach_quote">
                                                "Pun un accent deosebit pe <strong className="text-terracotta font-semibold">relația terapeutică</strong>, considerând-o fundamentul oricărui proces de schimbare reușit."
                                            </Trans>
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Specializations Grid Card */}
                            <div className="bg-white dark:bg-stone-900 rounded-3xl p-8 sm:p-10 shadow-elevated border border-stone-200/80 dark:border-stone-800 transition-colors">
                                <h3 className="font-display text-2xl sm:text-3xl font-bold text-stone-900 dark:text-stone-100 mb-6 flex items-center">
                                    <div className="w-8 h-8 rounded-lg bg-terracotta/10 text-terracotta flex items-center justify-center mr-3 flex-shrink-0">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                    </div>
                                    {t('about.spec_title')}
                                </h3>
                                
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {specializations.map((item, index) => (
                                        <div key={index} className="bg-stone-50 dark:bg-stone-800/60 rounded-2xl p-5 border border-stone-200/60 dark:border-stone-700 hover:border-terracotta-200 dark:hover:border-terracotta transition-colors group">
                                            <div className="w-9 h-9 rounded-xl bg-white dark:bg-stone-700 shadow-2xs flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                                                {item.icon}
                                            </div>
                                            <h4 className="font-bold text-stone-900 dark:text-stone-100 text-sm sm:text-base mb-1 group-hover:text-terracotta transition-colors">
                                                {item.title}
                                            </h4>
                                            <p className="text-stone-500 dark:text-stone-400 text-xs sm:text-sm leading-relaxed">
                                                {item.desc}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                        </div>

                    </div>
                </div>
            </div>
        </>
    );
};

export default AboutPage;
